/**
 * Products API Service
 *
 * Handles product tracking operations (add/remove products).
 * Services are stateless and return promises - UI concerns belong in hooks.
 *
 * @see ../ARCHITECTURE.md for design principles
 */

import { authenticatedFetch } from '../../utils/authenticated-fetch';
import {
  ProductApiError,
  type AddProductsParams,
  type AddProductsResult,
  type RemoveProductsParams,
  type RemoveProductsResult,
} from './types';

/**
 * Add products to tracking (via onboarding complete endpoint)
 *
 * @param params - Product IDs and optional thresholds to add
 * @returns Result with success status and count
 * @throws ProductApiError if the operation fails
 */
export async function addProducts(params: AddProductsParams): Promise<AddProductsResult> {
  const { productIds, thresholds } = params;

  const response = await authenticatedFetch('/api/onboarding/complete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ productIds, thresholds }),
  });

  const data = (await response.json()) as AddProductsResult;

  if (!response.ok || !data.success) {
    throw new ProductApiError(
      data.error || 'Failed to add products',
      response.status
    );
  }

  return data;
}

/**
 * Remove products from tracking
 *
 * @param params - Variant IDs to remove
 * @returns Result with success status and count
 * @throws ProductApiError if the operation fails
 */
export async function removeProducts(params: RemoveProductsParams): Promise<RemoveProductsResult> {
  const { variantIds } = params;

  const response = await authenticatedFetch('/api/tracked-products', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ variantIds }),
  });

  const data = (await response.json()) as RemoveProductsResult;

  if (!response.ok || !data.success) {
    throw new ProductApiError(
      data.error || 'Failed to remove products',
      response.status
    );
  }

  return data;
}

/**
 * Save product changes - handles both additions and removals
 *
 * @param toAdd - Product IDs to add
 * @param toRemove - Variant IDs to remove
 * @param thresholds - Optional threshold settings for added products
 * @returns Object with counts of added and removed items
 * @throws ProductApiError if any operation fails
 */
export async function saveProducts(
  toAdd: string[],
  toRemove: string[],
  thresholds?: { urgentDays: number; warningDays: number }
): Promise<{ added: number; removed: number }> {
  let added = 0;
  let removed = 0;

  // Handle additions
  if (toAdd.length > 0) {
    const result = await addProducts({ productIds: toAdd, thresholds });
    added = result.added ?? toAdd.length;
  }

  // Handle removals
  if (toRemove.length > 0) {
    const result = await removeProducts({ variantIds: toRemove });
    removed = result.removed ?? toRemove.length;
  }

  return { added, removed };
}
